/**
 * What the Evaluation page shows, and which re-evaluate scope is allowed.
 *
 * Copy and severity follow the Design-page frames. Record counts and dates
 * in those frames are sample data, so the sentences use the live model,
 * benchmark, and version and leave the sample counts out.
 */

import { providerLabelFor } from '@/features/model-setup/utils/teacherModelUtils';

export const SCOPE_FULL = 'full';
export const SCOPE_TEACHER_ONLY = 'teacher_only';
export const SCOPE_SPECIFIC_ONLY = 'specific_only';

const EYE_FIELDS = new Set([
    'teacher_system_prompt',
    'teacher_user_prompt',
    'teacher_schema',
    'student_system_prompt',
    'student_user_prompt',
    'student_schema',
]);

const SPECIFIC_FIELDS = new Set([
    'student_system_prompt',
    'student_user_prompt',
    'student_schema',
    'include_instruction_in_training',
]);

/** Setting names in the changes table (Figma 134:12383). */
export const FIELD_LABELS = {
    teacher_model: 'Commercial model',
    teacher_provider: 'Commercial model',
    teacher_system_prompt: 'System prompt',
    teacher_user_prompt: 'User prompt',
    teacher_schema: 'Output format',
    comparison_mode: 'Compare SpecificAI results',
    comparison_mode_option: 'Compare SpecificAI results',
    student_system_prompt: 'System prompt',
    student_user_prompt: 'User prompt',
    student_schema: 'Output format',
    include_instruction_in_training: 'Include prompt in training & evaluation',
};

function joinNames(value, fallback) {
    if (Array.isArray(value) && value.length) return value.filter(Boolean).join(', ');
    if (typeof value === 'string' && value) return value;
    return fallback;
}

function teacherPhrase(previous, current) {
    if (previous && current && previous !== current) {
        return `Teacher changed from ${previous} to ${current}`;
    }
    if (current) return `Teacher changed to ${current}`;
    return 'Teacher changed';
}

const SCOPE_NAMES = {
    [SCOPE_FULL]: 'Full evaluation',
    [SCOPE_TEACHER_ONLY]: 'Teacher only',
    [SCOPE_SPECIFIC_ONLY]: 'Specific model only',
};

export function primaryLabelFor(scope) {
    if (scope === SCOPE_TEACHER_ONLY) return 'Run Teacher Evaluation';
    if (scope === SCOPE_SPECIFIC_ONLY) return 'Run Specific Evaluation';
    return 'Run Full Evaluation';
}

function formatCount(value) {
    return Number(value).toLocaleString('en-US');
}

/** "1,200 records" / "1 record" / "" when the count is unknown. */
export function recordsLabel(count) {
    if (count == null || Number.isNaN(Number(count))) return '';
    return `${formatCount(count)} ${Number(count) === 1 ? 'record' : 'records'}`;
}

/**
 * Plan card title (Figma 36:2296): "Teacher only · sa_benchmark · 1,200 records"
 * for one benchmark, "Full evaluation · 2 benchmarks" for several.
 */
export function planTitle(scope, benchmarks) {
    const list = Array.isArray(benchmarks) ? benchmarks : [];
    const label = SCOPE_NAMES[scope] || SCOPE_NAMES[SCOPE_FULL];
    if (list.length === 1) {
        const [only] = list;
        const name = typeof only === 'string' ? only : only?.name;
        const records = typeof only === 'string' ? '' : recordsLabel(only?.records);
        return [label, name, records].filter(Boolean).join(' · ');
    }
    return `${label} · ${list.length} ${list.length === 1 ? 'benchmark' : 'benchmarks'}`;
}

/** Plan card body (Figma 199:27399 / 98:13302 / 199:27517). */
export function planBody(scope, { teacher, benchmarkNames = [], benchmarkChanged = false } = {}) {
    const model = teacher || 'the teacher';
    if (scope === SCOPE_TEACHER_ONLY) return `Runs ${model} only. Specific model results stay as they are.`;
    if (scope === SCOPE_SPECIFIC_ONLY) return 'Runs the Specific model only. Teacher results stay as they are.';
    if (benchmarkChanged) return `Runs ${model} and the Specific model. Both need to run on the updated benchmark.`;
    const target = benchmarkNames.length === 1 ? benchmarkNames[0] : 'the selected benchmarks';
    return `Runs ${model} and the Specific model on ${target}.`;
}

/** Scope card copy in Evaluation settings (Figma 35:2330). */
export function scopeCardBody(scope, teacher) {
    const model = teacher || 'the teacher';
    if (scope === SCOPE_TEACHER_ONLY) return `Runs ${model} only. Specific model results stay as they are.`;
    if (scope === SCOPE_SPECIFIC_ONLY) return 'Runs the Specific model only. Teacher results stay as they are.';
    return `Runs ${model} and the Specific model on the selected benchmarks.`;
}

function modelWithProvider(model, provider) {
    const label = providerLabelFor(provider);
    if (!model) return '';
    return label ? `${model} · ${label}` : model;
}

/**
 * Rows for the changes table: the model and provider swap collapse into one
 * "Commercial model" row ("gpt-4o · OpenAI → gpt-5 · OpenAI"), and the
 * comparison option folds into the comparison toggle when both changed.
 */
export function changeRows(changes, side, { teacherModel = '', teacherProvider = '' } = {}) {
    const rows = visibleChanges(changes, side);
    const model = rows.find((row) => row.field === 'teacher_model');
    const provider = rows.find((row) => row.field === 'teacher_provider');
    const comparisonToggled = rows.some((row) => row.field === 'comparison_mode');
    const out = [];
    let modelRowAdded = false;
    for (const row of rows) {
        if (row.field === 'comparison_mode_option' && comparisonToggled) continue;
        if (row.field === 'teacher_model' || row.field === 'teacher_provider') {
            if (modelRowAdded) continue;
            modelRowAdded = true;
            out.push({
                field: 'teacher_model',
                previous: modelWithProvider(
                    model ? model.previous : teacherModel,
                    provider ? provider.previous : teacherProvider,
                ),
                current: modelWithProvider(
                    model ? model.current : teacherModel,
                    provider ? provider.current : teacherProvider,
                ),
            });
            continue;
        }
        out.push(row);
    }
    return out;
}

export function benchmarkFields(prompt) {
    const found = [...String(prompt || '').matchAll(/\{([^{}]+)\}/g)]
        .map((match) => match[1].trim())
        .filter(Boolean);
    const unique = [...new Set(found)];
    return unique.length ? unique : ['example'];
}

export function autoMapColumns(fields, columns) {
    const byLower = new Map((columns || []).map((column) => [String(column).toLowerCase(), column]));
    return Object.fromEntries(fields.map((field) => [field, byLower.get(String(field).toLowerCase()) || '']));
}

export function mappingComplete(mapping, fields) {
    return (fields || []).every((field) => Boolean(mapping?.[field]));
}

/**
 * Line diff between two prompts: `removed` / `added` / `same` lines in order,
 * built from the longest common subsequence so unchanged lines stay as context.
 */
export function promptDiffLines(previous, current) {
    const before = previous == null || previous === '' ? [] : String(previous).split('\n');
    const after = current == null || current === '' ? [] : String(current).split('\n');
    const lcs = Array.from({ length: before.length + 1 }, () => new Array(after.length + 1).fill(0));
    for (let i = before.length - 1; i >= 0; i -= 1) {
        for (let j = after.length - 1; j >= 0; j -= 1) {
            lcs[i][j] = before[i] === after[j]
                ? lcs[i + 1][j + 1] + 1
                : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }
    const lines = [];
    let i = 0;
    let j = 0;
    while (i < before.length && j < after.length) {
        if (before[i] === after[j]) {
            lines.push({ kind: 'same', text: before[i] });
            i += 1;
            j += 1;
        } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
            lines.push({ kind: 'removed', text: before[i] });
            i += 1;
        } else {
            lines.push({ kind: 'added', text: after[j] });
            j += 1;
        }
    }
    while (i < before.length) lines.push({ kind: 'removed', text: before[i++] });
    while (j < after.length) lines.push({ kind: 'added', text: after[j++] });
    return lines;
}

/**
 * Diff rows for the full prompt view (Figma 134:12848): a removed line that
 * is directly followed by an added line renders as one `replaced` row
 * (struck-through old text, then the new text); other lines keep their kind.
 */
export function promptDiffRows(previous, current) {
    const lines = promptDiffLines(previous, current);
    const rows = [];
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const next = lines[index + 1];
        if (line.kind === 'removed' && next?.kind === 'added') {
            rows.push({ kind: 'replaced', removed: line.text, added: next.text });
            index += 1;
        } else {
            rows.push({ kind: line.kind, text: line.text });
        }
    }
    return rows;
}

export function visibleChanges(changes, side) {
    const rows = Array.isArray(changes) ? changes : [];
    if (side === 'specific') return rows.filter((row) => SPECIFIC_FIELDS.has(row.field));
    return rows.filter((row) => !SPECIFIC_FIELDS.has(row.field));
}

export function fieldHasEye(field) {
    return EYE_FIELDS.has(field);
}

/** "{n} lines added · {m} removed" counts for a prompt diff. */
export function promptDiffCounts(previous, current) {
    const lines = promptDiffLines(previous, current);
    return {
        added: lines.filter((line) => line.kind === 'added').length,
        removed: lines.filter((line) => line.kind === 'removed').length,
    };
}

const isEmptyValue = (value) => value == null || String(value).trim() === '';

/** Second line under a setting name in the changes table (Figma 134:12383). */
export function fieldSubLabel(field, previous, current) {
    if (field === 'teacher_model' || field === 'teacher_provider') return 'Teacher model';
    if (field === 'teacher_schema' || field === 'student_schema') return 'JSON schema';
    if (EYE_FIELDS.has(field)) {
        if (isEmptyValue(previous) && !isEmptyValue(current)) return 'Turned on';
        if (!isEmptyValue(previous) && isEmptyValue(current)) return 'Turned off';
        const { added, removed } = promptDiffCounts(previous, current);
        const changed = Math.max(added, removed);
        return `${changed} ${changed === 1 ? 'line' : 'lines'} changed`;
    }
    if (field === 'comparison_mode_option') return 'Comparison option';
    if (field === 'include_instruction_in_training') return 'Specific model';
    return 'Comparison';
}

/**
 * Display text for a non-prompt change value: On/Off for booleans and for
 * the schema toggle, — for empty.
 */
export function formatChangeValue(value, field = null) {
    if (value === true) return 'On';
    if (value === false) return 'Off';
    if (field === 'teacher_schema' || field === 'student_schema') return isEmptyValue(value) ? 'Off' : 'On';
    if (value == null || value === '') return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

/**
 * One-line banner for the Re-evaluate modal (Figma 199:27399 / 199:27517 /
 * 222:14534). The Evaluation page keeps the longer `text` sentence.
 */
function modalSummary({
    teacherModelChanged,
    teacherPromptChanged,
    specific,
    benchmarksChanged,
    previousTeacher,
    currentTeacher,
    benchmark,
}) {
    const parts = [];
    if (benchmarksChanged) parts.push(`Benchmark ${benchmark} was updated`);
    if (teacherModelChanged) parts.push(teacherPhrase(previousTeacher, currentTeacher));
    if (teacherPromptChanged && specific) parts.push('Specific model prompt and teacher prompt changed');
    else if (teacherPromptChanged) parts.push('Teacher prompt changed');
    else if (specific) parts.push('Specific model prompt changed');
    return parts.join(' · ');
}

/**
 * Page chrome for one combination of change flags and run status.
 *
 * `runStatus` is `running`, `failed`, or `success` only for a run the page
 * is currently showing. A historical completed evaluation is not `success`.
 */
export function resolveRevaluatePage({
    teacherModelChanged = false,
    teacherPromptChanged = false,
    specificPromptChanged = false,
    benchmarksChanged = false,
    datasetsChanged = false,
    comparisonOn = true,
    isGenerative = false,
    previousTeacher = '',
    currentTeacher = '',
    benchmarkName = '',
    versionLabel = '',
    runStatus = null,
    failureReason = '',
    progressText = '',
    lastScope = SCOPE_FULL,
} = {}) {
    const version = versionLabel || 'the previous version';
    const benchmark = joinNames(benchmarkName, 'the benchmark');
    const teacher = teacherPhrase(previousTeacher, currentTeacher);
    const model = currentTeacher || 'the teacher';
    const previous = previousTeacher || 'the previous teacher';
    const specific = specificPromptChanged && isGenerative;

    // Dataset / benchmark tags are informational only (Figma). Prompt tags open
    // View changes from the Evaluation page; opensDiff marks which ones do.
    const tags = [];
    if (benchmarksChanged) tags.push({ label: 'Benchmark updated', variant: 'warning', opensDiff: false });
    if (datasetsChanged) tags.push({ label: 'Dataset changed', variant: 'default', opensDiff: false });
    if (teacherModelChanged) tags.push({ label: 'Teacher changed', variant: 'default', opensDiff: true });
    if (teacherPromptChanged) tags.push({ label: 'Teacher prompt changed', variant: 'warning', opensDiff: true });
    if (specific) tags.push({ label: 'Specific prompt changed', variant: 'warning', opensDiff: true });

    const teacherSideChanged = teacherModelChanged || teacherPromptChanged;
    const bothPrompts = teacherPromptChanged && specific;
    const promptOrModel = teacherSideChanged || specific;
    const nothingChanged = !benchmarksChanged && !datasetsChanged && !promptOrModel;

    let defaultScope = SCOPE_FULL;
    let recommendedScope = null;
    if (!nothingChanged && !benchmarksChanged && !datasetsChanged) {
        if (bothPrompts || (specific && teacherModelChanged)) {
            defaultScope = SCOPE_FULL;
            recommendedScope = SCOPE_FULL;
        } else if (specific && !teacherSideChanged) {
            defaultScope = SCOPE_SPECIFIC_ONLY;
            recommendedScope = SCOPE_SPECIFIC_ONLY;
        } else if (teacherSideChanged) {
            defaultScope = SCOPE_TEACHER_ONLY;
            recommendedScope = SCOPE_TEACHER_ONLY;
        }
    } else if (benchmarksChanged && !datasetsChanged) {
        recommendedScope = SCOPE_FULL;
    }

    const lockedScopes = [];
    if (!comparisonOn || benchmarksChanged) lockedScopes.push(SCOPE_TEACHER_ONLY);
    if (!isGenerative) lockedScopes.push(SCOPE_SPECIFIC_ONLY);

    const base = {
        tags: nothingChanged ? [] : tags,
        defaultScope,
        recommendedScope: nothingChanged ? null : recommendedScope,
        lockedScopes,
        compareTeacherDisabled: Boolean(teacherModelChanged) && runStatus !== 'success',
        summary: modalSummary({
            teacherModelChanged,
            teacherPromptChanged,
            specific,
            benchmarksChanged,
            previousTeacher,
            currentTeacher,
            benchmark,
        }),
    };

    if (runStatus === 'running') {
        const progress = progressText ? ` · ${progressText}` : '';
        const lead = lastScope === SCOPE_SPECIFIC_ONLY
            ? `Re-evaluating the Specific model${progress}`
            : `Re-evaluating the teacher with ${model}${progress}`;
        return {
            ...base,
            severity: 'info',
            text: `${lead}. Results below stay available until it finishes.`,
            action: null,
            dismissible: false,
            evaluationButtonDisabled: true,
        };
    }

    if (runStatus === 'failed') {
        const reason = failureReason ? `: ${failureReason}` : '';
        return {
            ...base,
            severity: 'error',
            text: `Teacher evaluation stopped${reason}. Results below are still valid for ${previous}.`,
            action: 'Retry',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }

    if (runStatus === 'success') {
        const done = lastScope === SCOPE_SPECIFIC_ONLY
            ? 'Specific model re-evaluated. Results below are up to date.'
            : lastScope === SCOPE_FULL
                ? 'Re-evaluation finished. Results below are up to date.'
                : `Teacher re-evaluated with ${model}. Results below are up to date.`;
        return {
            ...base,
            compareTeacherDisabled: false,
            severity: 'success',
            text: done,
            action: null,
            dismissible: true,
            evaluationButtonDisabled: false,
        };
    }

    if (datasetsChanged && benchmarksChanged) {
        return {
            ...base,
            severity: 'warning',
            text: `Dataset changed since ${version} · Benchmark ${benchmark} was updated. Retrain to update the model and evaluate on the current benchmark.`,
            action: 'Retrain',
            dismissible: false,
            evaluationButtonDisabled: true,
        };
    }
    if (datasetsChanged && teacherSideChanged) {
        return {
            ...base,
            severity: 'info',
            text: `Dataset changed since ${version} · ${teacher}. Retrain to update the model and reflect the latest dataset and teacher.`,
            action: 'Retrain',
            dismissible: false,
            evaluationButtonDisabled: true,
        };
    }
    if (datasetsChanged) {
        return {
            ...base,
            severity: 'info',
            text: `Evaluation reflects ${version}, trained on an earlier dataset version. Retrain to build a model on the current dataset and generate updated evaluation metrics.`,
            action: 'Retrain',
            dismissible: false,
            evaluationButtonDisabled: true,
        };
    }
    if (benchmarksChanged && teacherSideChanged) {
        return {
            ...base,
            severity: 'warning',
            text: `Benchmark ${benchmark} was updated · ${teacher}. Results below reflect the previous benchmark and teacher configuration.`,
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }
    if (benchmarksChanged) {
        return {
            ...base,
            severity: 'warning',
            text: `Benchmark ${benchmark} was updated. Results below reflect the previous benchmark version, not the current one.`,
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }
    if (bothPrompts) {
        return {
            ...base,
            severity: 'warning',
            text: 'Specific model prompt and teacher prompt changed. Results below reflect the previous prompts.',
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }
    if (specific && teacherModelChanged) {
        return {
            ...base,
            severity: 'warning',
            text: `${teacher}. Specific model prompt changed. Results below reflect the previous prompt.`,
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }
    if (specific) {
        return {
            ...base,
            severity: 'warning',
            text: 'Specific model prompt changed. Results below reflect the previous prompt.',
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }
    if (teacherPromptChanged) {
        return {
            ...base,
            severity: 'warning',
            text: 'Teacher prompt changed. Results below reflect the previous prompt.',
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }
    if (teacherModelChanged) {
        return {
            ...base,
            severity: 'info',
            text: `${teacher}. Results below are still valid for ${previous}.`,
            action: 'Re-evaluate',
            dismissible: false,
            evaluationButtonDisabled: false,
        };
    }

    return {
        ...base,
        tags: [],
        severity: null,
        text: '',
        summary: '',
        action: null,
        dismissible: false,
        evaluationButtonDisabled: false,
        recommendedScope: null,
        defaultScope: SCOPE_FULL,
    };
}
